// Iter259 (Mensaje 3) · Pestaña Insights de Tender Intelligence — 4 tarjetas
// con datos SOLO de la cartera real (ofertas_comercial), nunca del scraper.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Loader2, Users, Swords, CalendarClock, Trophy, ExternalLink, AlertTriangle } from "lucide-react";
import { MetricInfo } from "@/components/MetricInfo";

const fmtEur = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

const Card = ({ icon, title, children, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <div className="h-7 w-7 rounded-lg bg-[#007934]/10 text-[#007934] flex items-center justify-center">{icon}</div>
      <h3 className="text-sm font-bold text-slate-800 font-['Outfit']">{title}</h3>
    </div>
    {children}
  </div>
);

const openFicha = (ofertaId) => window.open(`/ofertas-comercial?tab=list&open=${ofertaId}`, "_blank");

export function InsightsPanel({ onFilterClient }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rivalOpen, setRivalOpen] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/tender-intelligence/insights`);
        if (active) setData(d);
      } catch { /* noop */ }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, []);

  if (loading) return <div className="flex items-center justify-center py-16 text-slate-400" data-testid="ti-insights-loading"><Loader2 size={22} className="animate-spin"/></div>;
  if (!data) return <div className="text-sm text-slate-400 text-center py-10" data-testid="ti-insights-error">No se pudieron cargar los insights.</div>;

  const { concentracion, mapa_rivales, go_no_go_14d, win_rate_honesto, firmada_sin_proyecto_ado } = data;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ti-insights-panel">
      {/* 1. Concentración */}
      <Card icon={<Users size={14}/>} title="Concentración de clientes" testid="ti-insights-concentracion">
        <p className="text-xs text-slate-500 mb-2">{concentracion.resumen}</p>
        <div className="space-y-1 mb-3">
          {concentracion.top_clientes.map(c => (
            <button key={c.cliente} onClick={() => onFilterClient?.(c.cliente)}
              className="w-full flex items-center justify-between text-xs py-1 px-2 rounded hover:bg-emerald-50 transition-colors"
              data-testid={`ti-insights-client-${c.cliente}`}>
              <span className="text-slate-700 font-medium truncate">{c.cliente}</span>
              <span className="font-mono text-slate-500">{fmtEur(c.importe)}</span>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100 text-[11px]">
          <div>
            <div className="text-slate-400 font-semibold mb-1">Top provincias</div>
            {concentracion.top_provincias.map(p => <div key={p.provincia} className="flex justify-between text-slate-600"><span>{p.provincia}</span><span className="font-mono">{fmtEur(p.importe)}</span></div>)}
          </div>
          <div>
            <div className="text-slate-400 font-semibold mb-1">Top DG</div>
            {concentracion.top_dg.map(d => <div key={d.dg} className="flex justify-between text-slate-600"><span>{d.dg}</span><span className="font-mono">{fmtEur(d.importe)}</span></div>)}
          </div>
        </div>
      </Card>

      {/* 2. Mapa de rivales */}
      <Card icon={<Swords size={14}/>} title="Mapa de rivales" testid="ti-insights-rivales">
        <div className="overflow-x-auto max-h-72 overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead><tr className="text-slate-400 text-left border-b border-slate-100">
              <th className="py-1 pr-2">Competidor</th><th className="text-right px-1">Incumbent</th><th className="text-right px-1">Won vs VSM</th><th className="text-right px-1">Importe</th>
            </tr></thead>
            <tbody>
              {mapa_rivales.map(r => (
                <React.Fragment key={r.competidor}>
                  <tr className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => setRivalOpen(rivalOpen === r.competidor ? null : r.competidor)} data-testid={`ti-insights-rival-${r.competidor}`}>
                    <td className="py-1 pr-2 font-medium text-slate-700">{r.competidor}</td>
                    <td className="text-right px-1 text-slate-500">{r.n_incumbent}</td>
                    <td className="text-right px-1 text-rose-600 font-semibold">{r.n_won_vs_vsm}</td>
                    <td className="text-right px-1 font-mono text-slate-600">{fmtEur(r.importe)}</td>
                  </tr>
                  {rivalOpen === r.competidor && (
                    <tr><td colSpan={4} className="bg-slate-50 px-2 py-2">
                      <div className="text-slate-400 mb-1">Clientes: {r.clientes.join(", ") || "—"}</div>
                      <div className="flex flex-wrap gap-1">
                        {r.oferta_ids.map(id => (
                          <button key={id} onClick={() => openFicha(id)} className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-slate-200 hover:border-emerald-400 flex items-center gap-1" data-testid={`ti-insights-rival-oferta-${id}`}>
                            Ficha <ExternalLink size={9}/>
                          </button>
                        ))}
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* 3. Go / no-go 14 días */}
      <Card icon={<CalendarClock size={14}/>} title="Go / no-go · próximos 14 días" testid="ti-insights-gonogo">
        {go_no_go_14d.length === 0 ? (
          <p className="text-xs text-slate-400 py-4 text-center">Sin ofertas con presentación en los próximos 14 días.</p>
        ) : (
          <div className="overflow-x-auto max-h-72 overflow-y-auto">
            <table className="w-full text-[11px]">
              <thead><tr className="text-slate-400 text-left border-b border-slate-100">
                <th className="py-1 pr-2">Fecha</th><th className="pr-2">Cliente</th><th className="text-right px-1">Importe</th><th className="pr-1">Incumbent</th><th></th>
              </tr></thead>
              <tbody>
                {go_no_go_14d.map(g => (
                  <tr key={g.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`ti-insights-gonogo-${g.id}`}>
                    <td className="py-1 pr-2 font-mono text-slate-500">{g.fecha_presentacion}</td>
                    <td className="pr-2 text-slate-700 font-medium truncate max-w-[110px]">{g.cliente}</td>
                    <td className="text-right px-1 font-mono text-slate-600">{fmtEur(g.importe)}</td>
                    <td className="pr-1 text-slate-500 truncate max-w-[90px]">{g.incumbent || "—"}</td>
                    <td><button onClick={() => openFicha(g.id)} className="text-emerald-700 hover:underline flex items-center gap-0.5" data-testid={`ti-insights-gonogo-open-${g.id}`}>Ficha <ExternalLink size={9}/></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 4. Win-rate honesto */}
      <Card icon={<Trophy size={14}/>} title="Win-rate honesto" testid="ti-insights-winrate">
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
          <span className="text-xs text-slate-500 inline-flex items-center gap-1">
            Global
            <MetricInfo testId="metric-info-winrate-honesto" nombre="Win-rate honesto (Tender Intelligence / M0)"
              formula="Ganadas / (Ganadas + Perdidas) sobre licitaciones RESUELTAS de las que tenemos resultado real. Excluye las que seguimos sin saber (no se cuentan como perdidas)."
              poblacion="Histórico de inteligencia de mercado (US_BID360 M0), muestra distinta de la cartera comercial canónica. Puede tener N muy bajo (ej. 3 ganadas / 121 jugadas)."
              unidad="%"
              corte="Todo el histórico disponible, no un año YTD."
              filtros="⚠ No comparable con el Hit Rate del YTD Board ni con Adjudicadas/Presentadas de KPIs YTD: población y base distintas. N/D (—) significa sin resueltas, NO 0%."/>
          </span>
          <span className={`text-sm font-bold ${win_rate_honesto.global.pct == null ? "text-slate-400" : win_rate_honesto.global.pct >= 50 ? "text-emerald-700" : "text-rose-600"}`} data-testid="ti-insights-winrate-global">
            {win_rate_honesto.global.label}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 text-[11px] mb-2">
          <div>
            <div className="text-slate-400 font-semibold mb-1">Por DG</div>
            {Object.entries(win_rate_honesto.por_dg).map(([dg, wr]) => (
              <div key={dg} className="flex justify-between text-slate-600"><span className="truncate max-w-[90px]">{dg}</span><span className={wr.pct == null ? "text-slate-400" : "font-semibold"}>{wr.label}</span></div>
            ))}
          </div>
          <div>
            <div className="text-slate-400 font-semibold mb-1">Por zona (top 10)</div>
            {Object.entries(win_rate_honesto.por_zona).slice(0, 10).map(([z, wr]) => (
              <div key={z} className="flex justify-between text-slate-600"><span className="truncate max-w-[90px]">{z}</span><span className={wr.pct == null ? "text-slate-400" : "font-semibold"}>{wr.label}</span></div>
            ))}
          </div>
        </div>
        <div className="flex justify-between text-[11px] pt-2 border-t border-slate-100">
          <span className="text-slate-500">Tamaño &gt;20M€: <b className={win_rate_honesto.size_gt_20.pct == null ? "text-slate-400" : ""}>{win_rate_honesto.size_gt_20.label}</b></span>
          <span className="text-slate-500">Tamaño &lt;20M€: <b className={win_rate_honesto.size_lt_20.pct == null ? "text-slate-400" : ""}>{win_rate_honesto.size_lt_20.label}</b></span>
        </div>
        {firmada_sin_proyecto_ado > 0 && (
          <div className="mt-3 pt-2 border-t border-slate-100 flex items-center gap-1.5 text-[11px] text-amber-700" data-testid="ti-insights-firmada-sin-proyecto">
            <AlertTriangle size={12}/> {firmada_sin_proyecto_ado} oferta(s) FIRMADA/ADJUDICADA sin proyecto ADO vinculado
          </div>
        )}
      </Card>
    </div>
  );
}
